import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import AuthStore from "../../../stores/AuthStore";
import { AppLayout } from "../shared/layout";
import { getMyJobs, getJobStats } from "../../../data/requests/job-requests";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export function HomeScreen() {
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ["jobStats"],
    queryFn: () => getJobStats().then(res => res.data),
  });

  const { data: recentJobsData, isLoading: jobsLoading } = useQuery({
    queryKey: ["myJobs", { limit: 5 }],
    queryFn: () => getMyJobs({ limit: 5 }).then(res => res.data),
  });

  const stats = statsData || {};
  const recentJobs = recentJobsData?.jobs || [];

  const getStatusBadge = (status) => {
    const variants = {
      COMPLETED: "success",
      PENDING: "secondary",
      PROCESSING: "default",
      FAILED: "destructive",
      CANCELLED: "outline",
    };
    return <Badge variant={variants[status] || "default"}>{status}</Badge>;
  };

  return (
    <AppLayout title="Dashboard">
      <div className="space-y-8">
        <Card>
          <CardHeader>
            <CardTitle>Hoş Geldiniz, {AuthStore.user?.name || AuthStore.user?.email}</CardTitle>
            <CardDescription>AI Booth Dashboard</CardDescription>
          </CardHeader>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Toplam İşlem
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">
                {statsLoading ? "..." : stats.total || 0}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Tamamlanan
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                {statsLoading ? "..." : stats.completed || 0}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Beklemede
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-yellow-600">
                {statsLoading ? "..." : stats.pending || 0}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Başarısız
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-600">
                {statsLoading ? "..." : stats.failed || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Jobs */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Son İşlemler</CardTitle>
              <Button asChild variant="outline" size="sm">
                <Link to="/app/jobs">Tümünü Gör</Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {jobsLoading ? (
              <p className="text-muted-foreground">Yükleniyor...</p>
            ) : recentJobs.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">Henüz işlem bulunmuyor</p>
                <p className="text-sm text-muted-foreground">
                  Desktop uygulamasından fotoğraf çekip AI işlemi başlatabilirsiniz
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentJobs.map((job) => (
                  <div
                    key={job.id}
                    className="flex items-center justify-between border-b pb-4 last:border-0"
                  >
                    <div className="flex items-center gap-4">
                      {job.outputImageUrl ? (
                        <img
                          src={job.outputImageUrl}
                          alt="Result"
                          className="w-16 h-16 rounded object-cover"
                        />
                      ) : (
                        <img
                          src={job.inputImageUrl}
                          alt="Input"
                          className="w-16 h-16 rounded object-cover opacity-50"
                        />
                      )}
                      <div>
                        <p className="font-medium">Job #{job.id}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(job.createdAt).toLocaleString("tr-TR")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(job.status)}
                      <Button asChild variant="ghost" size="sm">
                        <Link to={`/app/jobs/${job.id}`}>Detay</Link>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}